<template>
	<div class="columns">
		<div class="column is-8">
			<p class="buttons is-centered">
				<button
					class="button"
					v-if="isLogged"
					:disabled="!enabledPrev"
					@click="prevPage"
				>
					{{ $t("pdf.previous-page") }}
				</button>
				<button class="button is-static">
					{{ currentPage }} / {{ numPages }}
				</button>
				<button
					class="button"
					v-if="isLogged"
					:disabled="!enabledNext"
					@click="nextPage"
				>
					{{ $t("pdf.next-page") }}
				</button>
			</p>
			<div ref="pdfContainer" class="pdf-container mx-1 relative">
				<DrawableBoard
					:width="width"
					:height="height"
					:thickness="drawSettings.thickness"
					:color="drawSettings.color"
					:signalRSocket="signalRSocket"
					:enabledDraw="isLogged"
					:index="1"
				/>
				<pdf
					:src="src"
					:page="currentPage"
					style="display: inline-block; width: 100%"
					@num-pages="numPages = $event"
					@page-loaded="currentPage = $event"
					@loaded="pdfLoaded"
				></pdf>
			</div>
		</div>
		<div class="column">
			<DrawSettings v-if="isLogged" v-model="drawSettings" />
			<DrawTools v-if="isLogged" :signalRSocket="signalRSocket" />
			<ConfirmSign v-if="signature" :signalRSocket="signalRSocket" />
		</div>
		<CaptureSignature
			v-if="captureSignature"
			@captured="signatureCaptured"
		/>
	</div>
</template>

<script>
import pdf from "vue-pdf";
import DrawableBoard from "../draw/DrawableBoard";
import DrawSettings from "../draw/DrawSettings";
import DrawTools from "../draw/DrawTools";
import ConfirmSign from "../draw/sign/ConfirmSign.vue";
import CaptureSignature from "../draw/sign/CaptureSignature";
import { mapActions, mapGetters } from "vuex";

export default {
	name: "pdf-viewer",
	components: {
		pdf,
		DrawableBoard,
		DrawSettings,
		DrawTools,
		ConfirmSign,
		CaptureSignature,
	},
	props: {
		fileId: { type: String, required: true },
		signalRSocket: { type: Object, required: true },
	},
	data() {
		return {
			src: "",
			currentPage: 0,
			numPages: undefined,

			width: 0,
			height: 0,
			loading: false,

			drawSettings: {
				color: "black",
				thickness: 1,
			},

			captureSignature: false,
		};
	},
	mounted() {
		this.loadPdf();

		this.signalRSocket.on(
			"ChangeDocumentPage",
			this.signalRChangeDocumentPage
		);

		this.signalRSocket.on("RequireSign", this.signalRRequireSign);

		this.signalRSocket.on(
			"ReloadSharedDocument",
			this.signalRReloadSharedDocument
		);
	},
	beforeDestroy() {
		this.signalRSocket.off(
			"ChangeDocumentPage",
			this.signalRChangeDocumentPage
		);

		this.signalRSocket.off("RequireSign", this.signalRRequireSign);

		this.signalRSocket.off(
			"ReloadSharedDocument",
			this.signalRReloadSharedDocument
		);
	},
	computed: {
		...mapGetters("conference", ["getConferenceId"]),
		...mapGetters("oidcStore", ["oidcIsAuthenticated"]),
		...mapGetters("signature", ["getSignature"]),
		isLogged() {
			return this.oidcIsAuthenticated;
		},
		enabledPrev() {
			return this.currentPage > 1;
		},
		enabledNext() {
			return this.currentPage < this.numPages;
		},
		signature() {
			return this.getSignature;
		},
	},
	watch: {
		currentPage() {
			let self = this;
			self.src.promise.then((pdf) => {
				pdf.getPage(self.currentPage).then(function () {
					let pdfCanvas =
						self.$refs.pdfContainer.getElementsByTagName(
							"canvas"
						)[1];

					// Await for page rended, vue-pdf dont have event for it
					setTimeout(() => {
						let width = getComputedStyle(pdfCanvas).width;
						self.width = parseFloat(width);

						let height = getComputedStyle(pdfCanvas).height;
						self.height = parseFloat(height);
						self.loading = false;

						self.setPageSize({
							width: self.width,
							height: self.height,
						});
					}, 1000);
				});
			});

			this.setPage({ page: this.currentPage });
			this.signalRSocket.invoke(
				"ClearBoard",
				this.getConferenceId,
				this.currentPage,
				false
			);
		},
	},
	methods: {
		...mapActions("colaborativeBoard", [
			"closeDocument",
			"setPage",
			"setPageSize",
		]),
		...mapActions("signature", ["setSignature", "setAddSignerName"]),
		loadPdf() {
			let url = `${process.env.VUE_APP_BACKEND_API_URL}api/files/share/${this.fileId}`;
			this.src = pdf.createLoadingTask(url);

			let self = this;
			self.src.promise.then((pdf) => {
				this.numPages = pdf.numPages;
			});
		},
		signalRChangeDocumentPage({ page }) {
			this.currentPage = page;
		},
		pdfLoaded() {
			this.currentPage++;
		},
		prevPage() {
			if (this.enabledPrev) {
				this.signalRSocket.invoke(
					"ChangeDocumentPage",
					this.getConferenceId,
					this.currentPage - 1
				);
			}
		},
		nextPage() {
			if (this.enabledNext) {
				this.signalRSocket.invoke(
					"ChangeDocumentPage",
					this.getConferenceId,
					this.currentPage + 1
				);
			}
		},
		signalRRequireSign(response) {
			window.console.log("signalRRequireSignAction");
			this.setAddSignerName({ addSignerName: response.addSignerName });
			this.captureSignature = true;
		},
		signalRReloadSharedDocument() {
			window.console.log("signalRReloadSharedDocument");
			this.currentPage = 0;
			this.loadPdf();
		},
		signatureCaptured(signature) {
			this.setSignature({
				signature: signature,
			});

			this.captureSignature = false;
		},
	},
};
</script>

<style lang="scss" scoped>
.pdf-container {
	max-height: 68vh;
	overflow-y: auto;
	background-color: darkgray;

	& span {
		padding: 1rem;
	}
}
</style>